import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store/store'
import {
	closeCartModal,
	updateCartQuantity,
	removeFromCart,
	clearCart,
} from '@/store/features/cart'
import {
	ShoppingCart,
	X,
	Minus,
	Plus,
	Trash2,
	ImageOff,
	ShoppingBag,
	Truck,
} from 'lucide-react'
import CartEntity from '@/entities/api/cart/cart'
import {
	attributeMaps,
	colorsMap,
	sizeMap,
	flavorMap,
	objectivesMap,
	modalitiesMap,
	NameAttributes,
} from '@/entities/api/products/product'
import { API_URL } from '@/services/api/keys'
import { verifyStockOfAttribute } from '@/services/api/products/attributes'
import Toast from '../elements/toast'
import formatCurrency from '@/utils/price-convert'
import {
	deleteCartProductById,
	updateCartProductQuantity,
	voidCartByCustomer,
} from '@/services/api/cart/cart'
import { ToastConfirm } from '../elements/toast-confirm'
import { saveLastRoute } from '@/utils/save-lastroute'
import { useNavigate } from 'react-router-dom'

const CartModal = ({
	customerId,
	token,
}: {
	customerId: string
	token: string
}) => {
	const dispatch = useDispatch<AppDispatch>()
	const navigate = useNavigate()
	const {
		cart,
		isCartModalOpen,
		totalItems,
		totalPrice,
		totalShipping,
		grandTotal,
	} = useSelector((state: RootState) => state.cart)

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isCartModalOpen) {
				dispatch(closeCartModal())
			}
		}

		document.addEventListener('keydown', handleEscape)
		return () => document.removeEventListener('keydown', handleEscape)
	}, [isCartModalOpen, dispatch])

	const handleQuantityChange = async (
		cartId: string,
		productId: string,
		attributeId: string,
		newQuantity: number
	) => {
		if (newQuantity >= 0) {
			const response = await verifyStockOfAttribute(
				productId ?? '',
				attributeId ?? ''
			)

			if ([400, 401, 409, 404].includes(response?.status ?? 0)) {
				Toast({
					type: 'error',
					message: 'El producto no se puede agregar al carrito',
				})
				return
			}

			const { quantity: quantityFromApi } = response?.data

			if (newQuantity > quantityFromApi) {
				Toast({
					type: 'info',
					message: `Solo quedan ${quantityFromApi} unidad(es) disponibles`,
				})
				return
			}

			if (customerId && token) {
				const responseChange = await updateCartProductQuantity(
					cartId,
					productId,
					attributeId,
					{ quantity: newQuantity },
					token
				)

				if ([400, 404, 401, 409].includes(responseChange.status ?? 0)) {
					Toast({ type: 'error', message: 'No se pudo actualizar la cantidad' })
				}
			}

			dispatch(
				updateCartQuantity({ productId, attributeId, quantity: newQuantity })
			)
		}
	}

	const handleRemoveItem = (cartId: string) => {
		ToastConfirm({
			message: '¿Estas seguro que quieres eliminar el producto del carrito?',
			onConfirm: async () => {
				if (customerId && token) {
					const response = await deleteCartProductById(cartId, token)
					if ([400, 401, 404, 409].includes(response?.status ?? 0)) {
						Toast({ type: 'error', message: 'No se pudo vaciar el carrito' })
						return
					}
				}
				dispatch(removeFromCart({ cartId }))
			},
			onCancel: async () => {
				Toast({ type: 'info', message: 'Se cancelo la operacion' })
			},
		})
	}

	const handleClearCart = () => {
		ToastConfirm({
			message: '¿Estas seguro que quieres vaciar el carrito?',
			onConfirm: async () => {
				if (customerId && token) {
					const response = await voidCartByCustomer(customerId, token)
					if ([400, 401, 404, 409].includes(response?.status ?? 0)) {
						Toast({ type: 'error', message: 'No se pudo vaciar el carrito' })
						return
					}
				}
				dispatch(clearCart())
			},
			onCancel: async () => {
				Toast({ type: 'info', message: 'Se cancelo la operacion' })
			},
		})
	}

	const calculateItemTotal = (item: CartEntity): number => {
		const attribute = item.product.attributes?.[0]
		if (!attribute) return 0

		const price = attribute.price || 0
		const quantity = attribute.quantity || 0
		const discount = item.product.discount || 0

		const discountedPrice = price - (price * discount) / 100
		return discountedPrice * quantity
	}

	const getAttributeDisplayValue = (attribute: any): string => {
		try {
			const values = Array.isArray(attribute.value)
				? attribute.value
				: [attribute.value]
			const names = Array.isArray(attribute.name)
				? attribute.name
				: [attribute.name]

			if (values.length === 0) return ''

			const displayValues = values.map((value: any, index: number) => {
				const name = names[index] || names[0]
				const valueStr = String(value)

				switch (name) {
					case NameAttributes.COLOR:
						return colorsMap[valueStr as keyof typeof colorsMap]
							? valueStr
							: valueStr
					case NameAttributes.SIZE:
						return sizeMap[valueStr as keyof typeof sizeMap]
							? `${valueStr} (${sizeMap[valueStr as keyof typeof sizeMap]})`
							: valueStr
					case NameAttributes.FLAVOR:
						return flavorMap[valueStr as keyof typeof flavorMap] || valueStr
					case NameAttributes.OBJECTIVE:
						return (
							objectivesMap[valueStr as keyof typeof objectivesMap] || valueStr
						)
					case NameAttributes.MODALITY:
						return (
							modalitiesMap[valueStr as keyof typeof modalitiesMap] || valueStr
						)
					default:
						return (
							attributeMaps[valueStr as keyof typeof attributeMaps] || valueStr
						)
				}
			})

			return displayValues.join(' / ')
		} catch (error) {
			const fallbackValue = Array.isArray(attribute.value)
				? attribute.value[0]
				: attribute.value
			return (
				attributeMaps[String(fallbackValue) as keyof typeof attributeMaps] ||
				String(fallbackValue || '')
			)
		}
	}

	const calculateItemShipping = (item: CartEntity): number => {
		if (!item.product.shipping_info) return 0

		const shippingInfo = item.product.shipping_info
		const attribute = item.product.attributes?.[0]
		const quantity = attribute?.quantity || 0

		if (
			!shippingInfo.isHaveShipping ||
			shippingInfo.type === 'free' ||
			quantity === 0
		) {
			return 0
		}

		if (shippingInfo.type === 'pay') {
			return shippingInfo.price || 0
		}

		return 0
	}

	if (!isCartModalOpen) return null

	return (
		<>
			<div
				className='fixed inset-0 bg-transparent z-[999998]'
				onClick={() => dispatch(closeCartModal())}
			/>

			<div className='fixed lg:absolute top-16 sm:top-16 lg:top-14 right-2 sm:right-4 lg:right-0 w-[85vw] sm:w-80 lg:w-96 bg-white border-0 rounded-xl shadow-2xl z-[9999999] max-h-[80vh] lg:max-h-[85vh] overflow-hidden backdrop-blur-sm'>
				<div className='flex items-center justify-between px-4 py-3 border-b border-gray-50 bg-gradient-to-r from-red-50 to-red-25'>
					<div className='flex items-center gap-2'>
						<div className='p-1.5 bg-red-100 rounded-lg'>
							<ShoppingCart className='w-4 h-4 text-red-600' />
						</div>
						<div>
							<h3 className='font-bold text-gray-900 text-base'>
								Carrito ({totalItems})
							</h3>
						</div>
					</div>
					<button
						onClick={() => dispatch(closeCartModal())}
						className='p-1.5 cursor-pointer hover:bg-white hover:shadow-md rounded-lg transition-all duration-200 group'
					>
						<X className='w-4 h-4 text-gray-400 group-hover:text-gray-600' />
					</button>
				</div>

				{cart.length === 0 ? (
					<div className='flex flex-col items-center justify-center p-8 text-center'>
						<div className='w-16 h-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl flex items-center justify-center mb-3 shadow-inner'>
							<ShoppingBag className='w-8 h-8 text-gray-300' />
						</div>
						<h4 className='font-semibold text-gray-700 mb-1 text-sm'>
							Tu carrito está vacío
						</h4>
						<p className='text-xs text-gray-500 mb-4'>
							Agrega productos para comenzar
						</p>
						<button
							onClick={() => dispatch(closeCartModal())}
							className='bg-gradient-to-r cursor-pointer from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm'
						>
							Continuar Comprando
						</button>
					</div>
				) : (
					<>
						<div className='max-h-[35vh] lg:max-h-[40vh] overflow-y-auto p-3 space-y-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50'>
							{cart.map((item) => {
								const attribute = item.product.attributes?.[0]
								if (!attribute) return null

								const itemTotal = calculateItemTotal(item)
								const itemShipping = calculateItemShipping(item)
								const originalPrice = attribute.price || 0
								const discount = item.product.discount || 0
								const discountedPrice =
									originalPrice - (originalPrice * discount) / 100

								return (
									<div
										key={item._id}
										className='group relative bg-gradient-to-br from-white to-gray-50 border border-gray-100 hover:border-gray-200 rounded-lg p-3 transition-all duration-200 hover:shadow-md'
									>
										<div className='flex gap-3'>
											<div className='w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0 border border-gray-100 shadow-sm'>
												{attribute.image?.url ? (
													<img
														src={`${API_URL}/${attribute.image.url}`}
														alt={item.product.name}
														className='w-full h-full object-cover'
													/>
												) : (
													<div className='w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100'>
														<ImageOff className='w-6 h-6 text-gray-300' />
													</div>
												)}
											</div>

											<div className='flex-1 min-w-0 space-y-1.5'>
												<div className='flex justify-between items-start'>
													<div className='flex-1 min-w-0 pr-2'>
														<h4 className='font-semibold text-gray-900 text-xs leading-tight truncate'>
															{item.product.name}
														</h4>
														<p className='text-xs text-gray-500 font-medium truncate'>
															{item.product.brand}
														</p>
													</div>
													<button
														onClick={() => handleRemoveItem(item?._id ?? '')}
														className='opacity-0 cursor-pointer group-hover:opacity-100 p-1 hover:bg-red-50 hover:text-red-600 rounded transition-all duration-200'
														title='Eliminar'
													>
														<Trash2 className='w-3.5 h-3.5' />
													</button>
												</div>

												<div className='flex items-center justify-between'>
													<span className='inline-block text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full font-medium'>
														{getAttributeDisplayValue(attribute)}
													</span>
													{discount > 0 && (
														<span className='text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold'>
															-{discount}%
														</span>
													)}
												</div>

												<div className='flex items-center justify-between'>
													<div className='flex flex-col'>
														{discount > 0 ? (
															<>
																<span className='text-sm font-bold text-red-600'>
																	{formatCurrency(discountedPrice)}
																</span>
																<span className='text-xs text-gray-400 line-through'>
																	{formatCurrency(originalPrice)}
																</span>
															</>
														) : (
															<span className='text-sm font-bold text-gray-900'>
																{formatCurrency(originalPrice)}
															</span>
														)}
														{itemShipping > 0 && (
															<div className='flex items-center gap-1 mt-0.5'>
																<Truck className='w-3 h-3 text-green-600' />
																<span className='text-xs text-green-600 font-medium'>
																	+{formatCurrency(itemShipping)}
																</span>
															</div>
														)}
													</div>

													<div className='flex items-center bg-white border border-gray-200 rounded-md shadow-sm'>
														<button
															onClick={() =>
																handleQuantityChange(
																	item?._id ?? '',
																	item.product._id!,
																	attribute._id!,
																	(attribute.quantity || 1) - 1
																)
															}
															className='p-1 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-50'
															disabled={attribute.quantity <= 1}
														>
															<Minus className='w-3 h-3 text-gray-600' />
														</button>
														<span className='px-2 py-1 text-xs font-bold text-gray-900 min-w-[2rem] text-center'>
															{attribute.quantity || 0}
														</span>
														<button
															onClick={() =>
																handleQuantityChange(
																	item?._id ?? '',
																	item.product._id!,
																	attribute._id!,
																	(attribute.quantity || 0) + 1
																)
															}
															className='p-1 cursor-pointer hover:bg-gray-50 transition-colors'
														>
															<Plus className='w-3 h-3 text-gray-600' />
														</button>
													</div>
												</div>

												<div className='text-right'>
													<span className='text-xs font-bold text-gray-900 bg-gray-50 px-2 py-0.5 rounded'>
														{formatCurrency(itemTotal)}
													</span>
												</div>
											</div>
										</div>
									</div>
								)
							})}
						</div>

						<div className='border-t border-gray-100 bg-gradient-to-t from-gray-50 to-white p-4 space-y-3'>
							<div className='space-y-2'>
								<div className='flex justify-between items-center'>
									<span className='text-sm text-gray-600'>Subtotal:</span>
									<span className='text-sm font-semibold text-gray-900'>
										{formatCurrency(totalPrice)}
									</span>
								</div>
								{totalShipping > 0 && (
									<div className='flex justify-between items-center'>
										<div className='flex items-center gap-1'>
											<Truck className='w-4 h-4 text-green-600' />
											<span className='text-sm text-gray-600'>Envío:</span>
										</div>
										<span className='text-sm font-semibold text-green-600'>
											{formatCurrency(totalShipping)}
										</span>
									</div>
								)}
								<div className='flex justify-between items-center pt-2 border-t border-gray-200'>
									<span className='text-base font-bold text-gray-900'>
										Total:
									</span>
									<span className='text-lg font-bold text-red-600 tracking-tight'>
										{formatCurrency(grandTotal)}
									</span>
								</div>
							</div>

							<div className='flex justify-between items-center pt-2'>
								<button
									onClick={handleClearCart}
									className='text-xs cursor-pointer text-gray-500 hover:text-red-600 transition-colors font-medium underline decoration-dotted underline-offset-2 hover:decoration-solid'
								>
									Vaciar carrito
								</button>
							</div>

							<div className='space-y-2'>
								<button
									onClick={() => {
										dispatch(closeCartModal())
										if (!customerId) {
											saveLastRoute({ route: '/Carrito', type: 'pay' })
											Toast({
												type: 'info',
												message: 'Inicia session para continuar con la compra',
											})
											navigate('/Login')
											return
										}
										navigate('/Carrito')
									}}
									className='block cursor-pointer w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white text-center py-3 rounded-xl font-bold transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5'
								>
									Abrir el carrito de compras
								</button>
								<button
									onClick={() => dispatch(closeCartModal())}
									className='w-full  cursor-pointer text-gray-600 hover:text-gray-800 py-2 text-sm font-medium transition-colors hover:bg-gray-50 rounded-lg'
								>
									Continuar comprando
								</button>
							</div>
						</div>
					</>
				)}
			</div>
		</>
	)
}

export default CartModal
